import React, { useEffect, useState } from 'react';
import { Linking, StyleSheet, TouchableOpacity, View, Platform, Switch } from 'react-native';
import { ActivityIndicator, Checkbox, Text, TextInput } from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useTranslation } from 'react-i18next';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useRegister } from '../../hooks/useAuth';
import { useLookupInvite } from '../../hooks/useFamilies';
import { useAuthStore } from '../../stores/authStore';
import { useAppColors } from '../../hooks/useAppColors';
import { getApiError } from '../../utils/formatters';
import LoadingIndicator from '../../components/common/LoadingIndicator';
import AuthLayout from './AuthLayout';
import GoogleSignInButton from './GoogleSignInButton';

type RootStackParamList = {
  SubUserRegister: { token: string };
  EmailVerification: { email: string; role: string; first_name: string; last_name: string; terms_accepted?: boolean };
  Login: undefined;
};

type SubUserRegisterRouteProp = RouteProp<RootStackParamList, 'SubUserRegister'>;
type SubUserRegisterNavigationProp = NativeStackNavigationProp<RootStackParamList>;

export default function SubUserRegisterScreen() {
  const navigation = useNavigation<SubUserRegisterNavigationProp>();
  const route = useRoute<SubUserRegisterRouteProp>();
  const { token } = route.params;
  const { t } = useTranslation();
  const colors = useAppColors();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [secureEntry, setSecureEntry] = useState(true);
  const [secureEntryConfirm, setSecureEntryConfirm] = useState(true);
  const [termsChecked, setTermsChecked] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setPendingInviteToken = useAuthStore((s) => s.setPendingInviteToken);

  // Look up invite details
  const { data: inviteData, isLoading: isLoadingInvite, error: inviteError } = useLookupInvite(token);

  const registerMutation = useRegister();

  const inputTheme = {
    colors: {
      onSurfaceVariant: colors.textSecondary,
      surface: colors.card,
    },
  };

  // Pre-fill first/last name from invite data if available
  useEffect(() => {
    if (inviteData) {
      setFirstName(inviteData.athlete_first_name);
      setLastName(inviteData.athlete_last_name);
    }
  }, [inviteData]);

  // Set the pending invite token in the store
  useEffect(() => {
    setPendingInviteToken(token);
    return () => {
      setPendingInviteToken(null);
    };
  }, [token, setPendingInviteToken]);

  const handleRegister = () => {
    const newErrors: Record<string, string> = {};
    if (!firstName.trim()) newErrors.firstName = t('auth.firstNameRequired');
    if (!lastName.trim()) newErrors.lastName = t('auth.lastNameRequired');
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) newErrors.email = t('auth.emailRequired');
    else if (!/^\S+@\S+\.\S+$/.test(trimmedEmail)) newErrors.email = t('auth.errors.invalidEmail');
    if (!password.trim()) newErrors.password = t('auth.passwordRequired');
    else if (password.length < 8) newErrors.password = t('auth.errors.passwordTooShort');
    if (!confirmPassword.trim()) newErrors.confirmPassword = t('auth.passwordRequired');
    else if (password !== confirmPassword) newErrors.confirmPassword = t('auth.passwordMismatch');
    if (!termsChecked) newErrors.terms = t('auth.termsRequired');
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    registerMutation.mutate(
      {
        email: trimmedEmail,
        password,
        name: `${firstName.trim()} ${lastName.trim()}`,
        role: 'athlete',
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      },
      {
        onSuccess: (result) => {
          if (!result.emailVerified) {
            navigation.navigate('EmailVerification', {
              email: trimmedEmail,
              role: 'athlete',
              first_name: firstName.trim(),
              last_name: lastName.trim(),
              terms_accepted: true,
            });
          }
        },
      },
    );
  };

  if (isLoadingInvite) {
    return <LoadingIndicator />;
  }

  if (inviteError || !inviteData) {
    return (
      <AuthLayout title={t('profile.invite.invalid')}>
        <View style={styles.errorContainer}>
          <Icon name="alert-circle" size={64} color={colors.error} />
          <Text style={[styles.errorMessage, { color: colors.textSecondary }]}>
            {inviteError ? String(inviteError) : t('profile.invite.notFound')}
          </Text>
          <TouchableOpacity
            onPress={() => navigation.navigate('Login' as never)}
            style={[styles.button, { backgroundColor: colors.accent }]}
            activeOpacity={0.8}
          >
            <Text style={[styles.buttonText, { color: colors.accentText }]}>
              {t('auth.backToLogin')}
            </Text>
          </TouchableOpacity>
        </View>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('profile.invite.welcome')}
      subtitle={t('profile.invite.invitedBy', { guardian: inviteData.guardian_name })}
      useKeyboardAvoiding
    >
      <View style={[styles.infoCard, { backgroundColor: colors.card }]}>
        <Text style={[styles.infoLabel, { color: colors.textSecondary }]}>
          {t('profile.invite.athleteName')}
        </Text>
        <Text style={[styles.infoValue, { color: colors.text }]}>
          {inviteData.athlete_first_name} {inviteData.athlete_last_name}
        </Text>
        {inviteData.athlete_dob && (
          <>
            <Text style={[styles.infoLabel, { color: colors.textSecondary }]}>
              {t('profile.dob')}
            </Text>
            <Text style={[styles.infoValue, { color: colors.text }]}>
              {new Date(inviteData.athlete_dob).toLocaleDateString()}
            </Text>
          </>
        )}
      </View>

      <View style={styles.row}>
        <TextInput
          label={t('auth.firstName')}
          value={firstName}
          onChangeText={(v) => { setFirstName(v); if (errors.firstName) setErrors((e) => ({ ...e, firstName: '' })); }}
          mode="outlined"
          error={!!errors.firstName}
          style={[styles.input, styles.halfInput]}
          outlineColor={colors.border}
          activeOutlineColor={colors.text}
          textColor={colors.text}
          theme={inputTheme}
        />
        <TextInput
          label={t('auth.lastName')}
          value={lastName}
          onChangeText={(v) => { setLastName(v); if (errors.lastName) setErrors((e) => ({ ...e, lastName: '' })); }}
          mode="outlined"
          error={!!errors.lastName}
          style={[styles.input, styles.halfInput]}
          outlineColor={colors.border}
          activeOutlineColor={colors.text}
          textColor={colors.text}
          theme={inputTheme}
        />
      </View>
      {(errors.firstName || errors.lastName) && (
        <Text style={[styles.fieldError, { color: colors.error }]}>
          {errors.firstName || errors.lastName}
        </Text>
      )}

      <TextInput
        label={t('auth.email')}
        value={email}
        onChangeText={(v) => { setEmail(v); if (errors.email) setErrors((e) => ({ ...e, email: '' })); }}
        mode="outlined"
        keyboardType="email-address"
        autoCapitalize="none"
        autoComplete="email"
        error={!!errors.email}
        style={styles.input}
        outlineColor={colors.border}
        activeOutlineColor={colors.text}
        textColor={colors.text}
        theme={inputTheme}
      />
      {errors.email && (
        <Text style={[styles.fieldError, { color: colors.error }]}>{errors.email}</Text>
      )}

      <TextInput
        label={t('auth.password')}
        value={password}
        onChangeText={(v) => { setPassword(v); if (errors.password) setErrors((e) => ({ ...e, password: '' })); }}
        mode="outlined"
        secureTextEntry={secureEntry}
        error={!!errors.password}
        right={
          <TextInput.Icon
            icon={secureEntry ? 'eye-off' : 'eye'}
            onPress={() => setSecureEntry(!secureEntry)}
            color={colors.textTertiary}
          />
        }
        style={styles.input}
        outlineColor={colors.border}
        activeOutlineColor={colors.text}
        textColor={colors.text}
        theme={inputTheme}
      />
      {errors.password && (
        <Text style={[styles.fieldError, { color: colors.error }]}>{errors.password}</Text>
      )}

      <TextInput
        label={t('auth.confirmPassword')}
        value={confirmPassword}
        onChangeText={(v) => { setConfirmPassword(v); if (errors.confirmPassword) setErrors((e) => ({ ...e, confirmPassword: '' })); }}
        mode="outlined"
        secureTextEntry={secureEntryConfirm}
        error={!!errors.confirmPassword}
        right={
          <TextInput.Icon
            icon={secureEntryConfirm ? 'eye-off' : 'eye'}
            onPress={() => setSecureEntryConfirm(!secureEntryConfirm)}
            color={colors.textTertiary}
          />
        }
        style={styles.input}
        outlineColor={colors.border}
        activeOutlineColor={colors.text}
        textColor={colors.text}
        theme={inputTheme}
      />
      {errors.confirmPassword && (
        <Text style={[styles.fieldError, { color: colors.error }]}>{errors.confirmPassword}</Text>
      )}

      <View style={styles.termsRow}>
        {Platform.OS === 'ios' ? (
          <Switch
            value={termsChecked}
            onValueChange={(v) => {
              setTermsChecked(v);
              if (errors.terms) setErrors((e) => ({ ...e, terms: '' }));
            }}
            trackColor={{ false: colors.border, true: colors.accent }}
            thumbColor={termsChecked ? colors.accentText : colors.textSecondary}
            style={{ marginRight: 8 }}
          />
        ) : (
          <Checkbox
            status={termsChecked ? 'checked' : 'unchecked'}
            onPress={() => {
              setTermsChecked(!termsChecked);
              if (errors.terms) setErrors((e) => ({ ...e, terms: '' }));
            }}
            color={colors.accent}
          />
        )}
        <Text style={[styles.termsText, { color: colors.text }]}>
          {t('auth.termsPrefix')}{' '}
          <Text
            style={[styles.termsLink, { color: colors.accent }]}
            onPress={() => (navigation.navigate as any)('PublicLegalDoc', { docType: 'privacy_policy' })}
          >
            {t('auth.privacyPolicy')}
          </Text>
          {' '}{t('auth.termsAnd')}{' '}
          <Text
            style={[styles.termsLink, { color: colors.accent }]}
            onPress={() => (navigation.navigate as any)('PublicLegalDoc', { docType: 'terms_of_service' })}
          >
            {t('auth.termsOfService')}
          </Text>
        </Text>
      </View>
      {errors.terms && (
        <Text style={[styles.fieldError, { color: colors.error }]}>{errors.terms}</Text>
      )}

      {registerMutation.isError && (
        <Text style={[styles.error, { color: colors.error }]}>
          {getApiError(registerMutation.error, t('auth.registrationFailed'), t)}
        </Text>
      )}

      <TouchableOpacity
        onPress={handleRegister}
        disabled={registerMutation.isPending}
        style={[styles.button, { backgroundColor: colors.accent }]}
        activeOpacity={0.8}
      >
        {registerMutation.isPending ? (
          <ActivityIndicator size="small" color={colors.accentText} />
        ) : (
          <Text style={[styles.buttonText, { color: colors.accentText }]}>
            {t('auth.register')}
          </Text>
        )}
      </TouchableOpacity>

      <GoogleSignInButton />

      <TouchableOpacity
        onPress={() => navigation.navigate('Login' as never)}
        style={styles.linkButton}
        activeOpacity={0.7}
      >
        <Text style={[styles.linkText, { color: colors.textSecondary }]}>
          {t('auth.alreadyHaveAccount')}{' '}
          <Text style={{ color: colors.text, fontWeight: '600' }}>
            {t('auth.signInLink')}
          </Text>
        </Text>
      </TouchableOpacity>
    </AuthLayout>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  input: {
    marginBottom: 12,
    backgroundColor: 'transparent',
  },
  halfInput: {
    flex: 1,
  },
  error: {
    textAlign: 'center',
    marginBottom: 12,
    fontSize: 13,
  },
  fieldError: {
    marginTop: -8,
    marginBottom: 8,
    marginLeft: 4,
    fontSize: 12,
  },
  button: {
    marginTop: 8,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  linkButton: {
    marginTop: 20,
    alignItems: 'center',
  },
  linkText: {
    fontSize: 14,
  },
  termsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 8,
    marginBottom: 8,
  },
  termsText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    paddingTop: 6,
  },
  termsLink: {
    textDecorationLine: 'underline',
    fontWeight: '500',
  },
  infoCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  infoLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4,
  },
  infoValue: {
    fontSize: 16,
    marginBottom: 12,
  },
  errorContainer: {
    alignItems: 'center',
    padding: 16,
  },
  errorMessage: {
    fontSize: 15,
    textAlign: 'center',
    marginTop: 12,
    marginBottom: 16,
  },
});
